/**
 * App-level command retry for the eventually-consistent backend.
 *
 * The HTTP client already retries network errors and 5xx responses (see
 * `src/adapters/index.ts`). This helper covers the *other* transient failure
 * class: a command issued against an aggregate that the backend hasn't
 * projected yet (e.g. configuring a feature on a connector that was created
 * moments earlier). Those come back as a non-accepted / 4xx response — the
 * HTTP layer leaves them alone — and previously surfaced as an error toast
 * that forced the user to refresh and resubmit. Retrying the command with a
 * short backoff lets the read/write model catch up and the request succeed.
 *
 * Only use this around *idempotent* commands (reconcile-by-id, import-by-id),
 * where issuing the same request twice is safe. Do not wrap multi-step
 * orchestration or anything that generates a fresh id per call.
 */

/** Default total attempts (1 initial + retries) for command retries. */
export const COMMAND_RETRY_MAX_ATTEMPTS = 3;

/** Default initial delay in ms between command retry attempts. */
export const COMMAND_RETRY_DELAY_MS = 600;

/** Default upper bound on the backoff delay in ms. */
export const COMMAND_RETRY_MAX_DELAY_MS = 4000;

export type RetryCommandOptions = {
  /** Total attempts including the first (default: COMMAND_RETRY_MAX_ATTEMPTS). */
  maxAttempts?: number;
  /** Initial delay between attempts in ms (default: COMMAND_RETRY_DELAY_MS). */
  delayMs?: number;
  /** Upper bound on the delay in ms (default: COMMAND_RETRY_MAX_DELAY_MS). */
  maxDelayMs?: number;
  /** Backoff strategy applied to delayMs (default: 'exponential'). */
  backoff?: 'fixed' | 'exponential';
  /**
   * Decide whether to retry after a failure. Receives the thrown error and the
   * attempt number that just failed (1-based). Defaults to retrying every
   * failure until attempts are exhausted — appropriate here because the
   * transient case is a non-accepted response with no status to discriminate on,
   * and these commands are idempotent.
   */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
};

/**
 * Runs `fn`, retrying with backoff when it throws. Rethrows the final error
 * once attempts are exhausted (or `shouldRetry` returns false), so callers keep
 * the original failure message.
 *
 * @example
 * await retryCommand(async () => {
 *   const res = await api.reconcileConnectorFeature(...);
 *   if (!extractAccepted(res)) throw new Error('Failed to reconcile connector feature');
 *   return true;
 * });
 */
export async function retryCommand<T>(
  fn: () => Promise<T>,
  options?: RetryCommandOptions
): Promise<T> {
  const maxAttempts = options?.maxAttempts ?? COMMAND_RETRY_MAX_ATTEMPTS;
  const delayMs = options?.delayMs ?? COMMAND_RETRY_DELAY_MS;
  const maxDelayMs = options?.maxDelayMs ?? COMMAND_RETRY_MAX_DELAY_MS;
  const backoff = options?.backoff ?? 'exponential';
  const shouldRetry = options?.shouldRetry ?? (() => true);

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt >= maxAttempts || !shouldRetry(error, attempt)) {
        throw error;
      }
      const backoffDelay =
        backoff === 'exponential'
          ? delayMs * Math.pow(2, attempt - 1)
          : delayMs;
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(backoffDelay, maxDelayMs))
      );
    }
  }

  // Unreachable: the loop either returns or throws, but satisfies the type checker.
  throw lastError;
}
